/* eslint-disable no-nested-ternary */
import * as three from "three";
import ParticleMesh from "../../instancing/Meshes/ParticleMesh";
import RectangleMesh from "../../instancing/Meshes/RectangleMesh";
import getMaterialWithUniforms from "../../instancing/materials/getMaterialWithUniforms";
import createCircleTexture from "../../instancing/textures/createCircleTexture";
import {
  pathogenicityYScale,
  pathogenicityRgbInd,
} from "../../constants/pathogenicity";
import {
  HETEROZYGOUS_1,
  SOMATIC,
  HOMOZYGOUS,
  HETEROZYGOUS_1_PHASED,
  HETEROZYGOUS_2_PHASED,
  HOMOZYGOUS_PHASED,
  MITO,
} from "../../constants/zygosity";
import createZygosityTexture from "../../instancing/textures/createZygosityTexture";

const pointSize = 12;
const selectionSize = 22;
const size = 32;

const zygTextDict = {
  [SOMATIC]: 0,
  [MITO]: 0,
  [HETEROZYGOUS_1]: 2,
  [HOMOZYGOUS]: 3,
  [HETEROZYGOUS_1_PHASED]: 6,
  [HETEROZYGOUS_2_PHASED]: 5,
  [HOMOZYGOUS_PHASED]: 3,
};

const zygText = createZygosityTexture({
  size: 4 * size,
  strokeWidth: 2 * 4 * (size / (4 * pointSize)),
});

const circleSelectionText = createCircleTexture({
  size,
  strokeWidth: 2 * (size / selectionSize),
});

export function getPetalsMesh() {
  const conf = {
    posX: 1,
    posY: 1,
    offset: 1,
    rgbaCode: 1,
    textureCoordsInd: 1,
  };

  const matconf = {
    depthWrite: false,
    depthTest: false,
    transparent: true,
    blending: three.NormalBlending,
    alphaTest: 0.1,
  };

  const materialConf = getMaterialWithUniforms(matconf)
    .setOffset([0, 0])
    .setPaddingX([1, -1])
    .setPaddingY([0, 0, 0, 0])
    .setPointSize(pointSize)
    .setColorMap(Array(10).fill(new three.Vector4()))
    .setTexture(zygText.texture)
    .setTextureCoordMap(zygText.textureCoordsMap);

  return new ParticleMesh(materialConf, conf);
}

export function getPhasingGroupMesh() {
  const conf = { posX: 1, rgbaCode: 1, posY: 1 };
  const y = -30;
  const height = 4;
  const materialConf = getMaterialWithUniforms()
    .setOffset([0, 0])
    .setPaddingX([-0.5, 0.5, -0.5, 0.5])
    .setPaddingY([y, y, y + height, y + height])
    .setColorMap(Array(10).fill(new three.Vector4()));

  return new RectangleMesh(materialConf, conf);
}

export function getLollipopBaseMesh() {
  const conf = { posX: 1, posY: 1, rgbaCode: 1 };
  const materialConf = getMaterialWithUniforms({ disableRaycast: true })
    .setOffset([0, 0])
    .setPaddingX([-0.5, 0.5, -0.5, 0.5])
    .setPaddingY([0, 0, 0, 0])
    .setColorMap(Array(10).fill(new three.Vector4()));

  return new RectangleMesh(materialConf, conf);
}

export function getSelectionMesh() {
  const matconf = {
    disableRaycast: true,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  };
  const conf = {
    posX: 1,
    posY: 1,
    offset: 1,
    rgbaCode: 1,
  };
  const materialConf = getMaterialWithUniforms(matconf)
    .setOffset([0, 0])
    .setPaddingX([0, 0])
    .setPaddingY([0, 0, 0, 0])
    .setPointSize(selectionSize)
    .setColorMap([new three.Vector4(0.5, 0.5, 0.5, 0)])
    .setTexture(circleSelectionText.texture)
    .setTextureCoordMap(circleSelectionText.textureCoordsMap);
  return new ParticleMesh(materialConf, conf);
}

function getTextureCoord(zygosity) {
  return zygTextDict[zygosity];
}

export function drawPetal(petals, mesh, colorByPhasingGroup, colorByImpact) {
  const count = petals.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  const setOffset = mesh.updateOffset({ count, isAppend });
  const setTextureCoordInd = mesh.updateTextureCoordsInd({ count, isAppend });

  for (let i = 0; i < count; i++) {
    const petal = petals[i];
    const { detail, index, lollipop } = petal;
    const { variant, zygosity } = detail;
    const rgb = colorByPhasingGroup
      ? detail.phasingGroup?.indMod || 0
      : colorByImpact
      ? detail.codingColorIndex
      : pathogenicityRgbInd[detail.pathoIndex];

    setX(i, variant.region.g0 + 0.5);
    setRgb(i, rgb);
    setTextureCoordInd(i, getTextureCoord(zygosity));

    if (lollipop.petalSize) {
      const offset = getPetalOffset(index, lollipop.petalSize);
      setOffset(i, offset[0], offset[1]);
    }
  }
}

export function updatePetalColor(items, mesh, colorByPhase, colorByImpact) {
  const setRgb = mesh.updateRgba({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const { pathoIndex, phasingGroup, codingColorIndex } = items[i].detail;

    const rgbInd = colorByPhase
      ? phasingGroup?.indMod || 0
      : colorByImpact
      ? codingColorIndex
      : pathogenicityRgbInd[pathoIndex];

    setRgb(i, rgbInd);
  }
}

export function updateBaseColor(items, mesh, colorByPhasingGroup) {
  const setRgb = mesh.updateRgba({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const petal = items[i];
    const { pathoIndex, detail } = petal;
    const rgb = colorByPhasingGroup
      ? detail.phasingGroup?.indMod || 0
      : pathogenicityRgbInd[pathoIndex];

    setRgb(i, rgb, rgb, rgb, rgb);
  }
}

export function updateVisPetal(items, mesh, filters) {
  const impactFils = new Set(filters.filter((x) => x.indexOf("impact") === 0));
  const zygFils = new Set(filters.filter((x) => x.indexOf("zygosity") === 0));
  const ampFils = new Set(filters.filter((x) => x.indexOf("amp") === 0));
  const pathFils = new Set(filters.filter((x) => x.indexOf("path") === 0));
  const sampleFilsAny = filters.filter((x) => x.match(/^any-sample/));
  const sampleFilsAll = filters.filter((x) => x.match(/^all-sample/));
  const sampleFilsNot = filters.filter((x) => x.match(/^not-sample/));
  const coverageFilter = filters.find((x) => x.indexOf("coverage") === 0);
  const coverage = Number((coverageFilter || "coverage-0").split("-")[1]);
  const samplesAny = sampleFilsAny.map((x) => x.replace(/^any-sample-/, ""));
  const samplesAll = sampleFilsAll.map((x) => x.replace(/^all-sample-/, ""));
  const samplesNot = sampleFilsNot.map((x) => x.replace(/^not-sample-/, ""));
  const cnvOverlaps = filters.filter((x) => x.match(/^overlap/));
  const overlapSamples = cnvOverlaps.map((x) => x.replace(/^overlap-/, ""));
  const overSams = overlapSamples;

  const checkNot = sampleFilsNot.length === 0;
  const checkAll = sampleFilsAll.length === 0;
  const checkAny = sampleFilsAny.length === 0;
  const checkOverlap = cnvOverlaps.length === 0;

  const setY = mesh.updateY({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const a = items[i];
    const t = items[i].detail;
    const { pathoIndex, codingImpactIndex, zygosity, tier } = t;
    const ciKey = `impact-${codingImpactIndex}`;
    const zygKey = `zygosity-${zygosity}`;
    const ampKey = `amp-${tier}`;
    const pathKey = `pathogenicity-${pathoIndex}`;
    const hasAnySample = checkAny || a.containedInAny(samplesAny);
    const hasAllSample = checkAll || a.containedInAll(samplesAll);
    const hasNotSample = checkNot || !a.containedInAny(samplesNot);
    const hasNotOverlap = checkOverlap || a.overlapWithCNVSamples(overSams);
    const hasNotCov = coverage === 0 || t.coverage > coverage;
    const hasImpact = impactFils.size === 0 || impactFils.has(ciKey);
    const hasZygosity = zygFils.size === 0 || zygFils.has(zygKey);
    const hasAmp = ampFils.size === 0 || ampFils.has(ampKey);
    const hasPath = pathFils.size === 0 || pathFils.has(pathKey);

    const isVisible =
      hasImpact &&
      hasZygosity &&
      hasAmp &&
      hasPath &&
      hasAnySample &&
      hasAllSample &&
      hasNotSample &&
      hasNotCov &&
      hasNotOverlap;
    items[i].isVisible = isVisible;

    const y = isVisible ? pathogenicityYScale[pathoIndex] : -5000;

    setY(i, y);
  }
}

export function updateVisBase(items, mesh) {
  const setY = mesh.updateY({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const petal = items[i];
    const { pathoIndex } = petal;

    if (petal.isVisible) {
      const y = pathogenicityYScale[pathoIndex];
      if (y > 0.5) {
        setY(i, 0.5, 0.5, y, y);
      } else {
        setY(i, y, y, 0.5, 0.5);
      }
    } else {
      setY(i, -5000, -5000, -5000, -5000);
    }
  }
}

export function drawSelection(petals, mesh) {
  const count = petals.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setOffset = mesh.updateOffset({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const petal = petals[i];
    const { detail, index, lollipop } = petal;
    const { variant } = detail;
    const x = variant.region.g0 + 0.5;
    setX(i, x);

    if (lollipop.petalSize) {
      const offset = getPetalOffset(index, lollipop.petalSize);
      setOffset(i, offset[0], offset[1]);
    }
  }
}

export function drawBase(petals, mesh, colorByPhasingGroup) {
  const count = petals.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const petal = petals[i];
    const { region, pathoIndex, detail } = petal;
    const x = region.g0 + 0.5;
    const rgb = colorByPhasingGroup
      ? detail.phasingGroup?.indMod || 0
      : pathogenicityRgbInd[pathoIndex];
    setX(i, x, x, x, x);
    setRgb(i, rgb, rgb, rgb, rgb);
  }
}

export function drawPhasingGroup(groups, mesh) {
  const count = groups.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const phasingGroup = groups[i];
    const { region, indMod } = phasingGroup;
    const x1 = region.g0;
    const x2 = region.g1 + 1;
    const rgb = indMod;

    setX(i, x1, x2, x1, x2);
    setRgb(i, rgb, rgb, rgb, rgb);
  }
}

function getPetalOffsetRadius(total) {
  if (total === 1) {
    return 0;
  }
  if (total === 2) {
    return pointSize * 0.4;
  }
  if (total === 3) {
    return pointSize * 0.6;
  }
  if (total < 6) {
    return pointSize;
  }
  return pointSize * 1.2;
}

function getPetalOffset(index, total) {
  if (total === 1 || (total > 3 && index === 0)) {
    return [0, 0];
  }
  const radius = getPetalOffsetRadius(total);
  const shift = total > 3 ? 1 : 0;
  const indexPercent = (index - shift) / (total - shift);
  const radian = 2 * Math.PI * indexPercent;
  const x = Math.sin(radian) * radius;
  const y = Math.cos(radian) * radius;
  return [x, y];
}
